import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../../utils/focusTrap';
import styles from './Modal.module.css';

export type ModalSize = 'small' | 'medium' | 'large';

export interface ModalProps {
  /**
   * Controls modal visibility
   */
  isOpen: boolean;

  /**
   * Modal title (required for accessibility)
   */
  title: string;

  /**
   * Callback when modal should close (escape key or overlay click)
   */
  onClose: () => void;

  /**
   * Modal content
   */
  children: React.ReactNode;

  /**
   * Modal size variant
   * @default 'medium'
   */
  size?: ModalSize;

  /**
   * Disable closing on overlay click
   * @default false
   */
  disableOverlayClick?: boolean;

  /**
   * Disable closing on escape key
   * @default false
   */
  disableEscapeKey?: boolean;

  /**
   * Optional subtitle/description
   */
  subtitle?: React.ReactNode;

  /**
   * ID for the modal title (auto-generated if not provided)
   */
  titleId?: string;

  /**
   * Additional CSS class for modal container
   */
  className?: string;
}

/**
 * Modal Component
 *
 * Accessible, keyboard-navigable modal dialog following Bauhaus design principles.
 *
 * Features:
 * - Focus trap (tab navigation contained within modal)
 * - Escape key to close (configurable)
 * - Overlay click to close (configurable)
 * - Three size variants: small (400px), medium (500px), large (700px)
 * - Proper ARIA attributes
 * - Hard-edged geometric shadow
 *
 * @example
 * ```tsx
 * <Modal
 *   isOpen={showModal}
 *   title="Create Application"
 *   onClose={() => setShowModal(false)}
 *   size="medium"
 * >
 *   <form>...</form>
 * </Modal>
 * ```
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  title,
  onClose,
  children,
  size = 'medium',
  disableOverlayClick = false,
  disableEscapeKey = false,
  subtitle,
  titleId,
  className,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const generatedTitleId = useRef(`modal-title-${Math.random().toString(36).substr(2, 9)}`);
  const finalTitleId = titleId || generatedTitleId.current;

  // Enable focus trap
  useFocusTrap(modalRef, isOpen);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || disableEscapeKey) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, disableEscapeKey, onClose]);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (!disableOverlayClick) {
      onClose();
    }
  };

  const sizeClass = {
    small: styles.modalSmall,
    medium: styles.modalMedium,
    large: styles.modalLarge,
  }[size];

  // Use portal to render at document.body, avoiding nested form issues
  return createPortal(
    <div
      className={styles.modalOverlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={finalTitleId}
    >
      <div
        ref={modalRef}
        className={`${styles.modal} ${sizeClass} ${className || ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={finalTitleId} className={styles.modalTitle}>
          {title}
        </h2>
        {subtitle && (
          <div className={styles.modalSubtitle}>
            {subtitle}
          </div>
        )}
        <div className={styles.modalContent}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};
